import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Building2,
  CalendarDays,
  Download,
  AppWindow
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { lobsApi } from '../../api/lobs';
import { appVersionApi } from '../../api/appVersion';

const NAV_ID = 'sidebar-main-menu';

function isMenuItemActive(path, isActive, pathname) {
  if (path === '/lobs') {
    return (
      pathname === '/lobs'
      || pathname.startsWith('/lobs/')
      || pathname.startsWith('/functions/')
    );
  }
  return isActive;
}

function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isSuperadmin = user?.role === 'superadmin';
  const isEmployee = user?.role === 'employee';

  const [isHead, setIsHead] = useState(false);
  useEffect(() => {
    if (isSuperadmin || isEmployee) return undefined;
    let active = true;
    lobsApi.list()
      .then((res) => { if (active) setIsHead((res.data || []).length > 0); })
      .catch((err) => { if (active) console.error('[Sidebar] Failed to load LOBs for nav', err); });
    return () => { active = false; };
  }, [isSuperadmin, isEmployee, user?.id]);

  const employeeMenu = [
    { path: '/dashboard', label: 'My Dashboard', icon: LayoutDashboard },
  ];

  const [desktopUrl, setDesktopUrl] = useState(null);
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    appVersionApi.latest('windows')
      .then((res) => { if (active) setDesktopUrl(res.data?.downloadUrl || null); })
      .catch(() => { if (active) setDesktopUrl(null); });
    return () => { active = false; };
  }, [user?.id]);

  const adminMenu = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/employees', label: 'Employees', icon: Users },
    { path: '/reports', label: 'Reports', icon: FileText },
    isSuperadmin
      ? { path: '/lobs', label: 'Line of Businesses', icon: Building2 }
      : (isHead ? { path: '/lobs', label: 'My LOBs', icon: Building2 } : null),
    { path: '/app-catalog', label: 'App Catalog', icon: AppWindow, superadminOnly: true },
    { path: '/holidays', label: 'Holidays', icon: CalendarDays, superadminOnly: true },
    { path: '/settings', label: 'Settings', icon: Settings, superadminOnly: true },
  ].filter(Boolean).filter((item) => !item.superadminOnly || isSuperadmin);

  const menuItems = isEmployee ? employeeMenu : adminMenu;

  return (
      <aside
        aria-label="Main navigation"
        className={`${
          collapsed ? 'w-16' : 'w-56'
        } bg-[#151821] fixed inset-y-0 left-0 shadow-xl transition-all duration-300 z-50 border-r border-[#3A4256] flex flex-col overflow-hidden`}
      >
        <div
          className={`relative h-12 border-b border-[#3A4256] flex items-center shrink-0 ${
            collapsed ? 'px-0' : 'justify-between px-3'
          }`}
        >
          {!collapsed && (
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/myworkmate-logo.png"
                alt=""
                className="h-8 w-8 object-contain flex-shrink-0"
              />
              <h1 className="text-sm font-bold text-white truncate">
                MyWorkmate
              </h1>
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={onToggle}
              className="p-1.5 rounded-[4px] hover:bg-white/10 transition-colors text-white/60 hover:text-white flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151821]"
              aria-expanded
              aria-controls={NAV_ID}
              aria-label="Collapse navigation sidebar"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          {collapsed && (
            <div className="flex w-full items-center justify-between px-1.5">
              <img
                src="/myworkmate-logo.png"
                alt="MyWorkmate home"
                className="h-6 w-6 object-contain flex-shrink-0"
              />
              <button
                type="button"
                onClick={onToggle}
                className="sidebar-expand-tab"
                aria-expanded={false}
                aria-controls={NAV_ID}
                aria-label="Expand navigation sidebar"
              >
                <ChevronRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" strokeWidth={2.25} />
              </button>
            </div>
          )}
        </div>

        <div
          className={`sidebar-nav flex-1 min-h-0 ${
            collapsed ? 'sidebar-nav--rail' : 'overflow-y-auto'
          }`}
        >
          <div className="min-h-full flex flex-col">
            <nav
              id={NAV_ID}
              className={`py-3 ${collapsed ? 'px-1' : 'px-2'}`}
              aria-label="Main menu"
            >
              {!collapsed && (
                <p className="px-2 mb-2 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
                  Menu
                </p>
              )}
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  aria-label={collapsed ? item.label : undefined}
                  className={({ isActive }) => {
                    const active = isMenuItemActive(item.path, isActive, pathname);
                    return `sidebar-nav-link group flex items-center gap-2 rounded-[4px] mb-1 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151821] ${
                      collapsed ? 'justify-center w-10 h-9 mx-auto p-0' : 'px-2 py-2'
                    } ${
                      active
                        ? 'sidebar-nav-link--active brand-gradient-primary shadow-md'
                        : ''
                    }`;
                  }}
                >
                  {({ isActive }) => {
                    const active = isMenuItemActive(item.path, isActive, pathname);
                    return (
                      <>
                        <item.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        {collapsed ? (
                          <span className="sr-only">{item.label}</span>
                        ) : (
                          <span className="text-sm font-medium truncate">{item.label}</span>
                        )}
                        {active && !collapsed && <span className="sr-only">(current page)</span>}
                      </>
                    );
                  }}
                </NavLink>
              ))}
            </nav>

            {!collapsed && (
              <div className="mt-auto px-2 py-3 border-t border-[#3A4256]">
                {desktopUrl && (
                  <a
                    href={desktopUrl}
                    className="flex items-center gap-2 px-2 py-2 mb-1 rounded-[4px] text-white/70 hover:bg-white/10 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <Download className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm font-medium">Get the desktop app</span>
                  </a>
                )}
                <div className="px-2 py-1.5 bg-white/5 rounded-[4px]">
                  <p className="text-[10px] text-white/55">Version 1.0.0</p>
                  <p className="text-[10px] text-white/40">© {new Date().getFullYear()} Amzur Technologies</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
  );
}

export default Sidebar;
